import { useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ALLOWED_MIMES, MAX_FILE_SIZE_BYTES } from '../schema/attachmentSchema';
import { ProgressBar } from './ProgressBar';

// ─── Keyframes ─────────────────────────────────────────────────────────────

const KEYFRAME_CSS = `
@keyframes attach-spin { to { transform: rotate(360deg); } }
`;

// ─── Helpers ───────────────────────────────────────────────────────────────

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ─── Props ─────────────────────────────────────────────────────────────────

export interface DropZoneProps {
  onFileDrop: (file: File) => void;
  isUploading: boolean;
  uploadProgress: number;
  uploadingFileName?: string;
  /** Highlights the dropzone border when list is empty (invite gesture) */
  emphasize?: boolean;
  disabled?: boolean;
}

// ─── DropZone ──────────────────────────────────────────────────────────────

export function DropZone({
  onFileDrop,
  isUploading,
  uploadProgress,
  uploadingFileName,
  emphasize = false,
  disabled = false,
}: DropZoneProps) {
  const { t } = useTranslation();
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFiles = useCallback(
    (files: FileList | null) => {
      if (!files || files.length === 0) return;
      const file = files[0];
      onFileDrop(file);
    },
    [onFileDrop]
  );

  const onDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled && !isUploading) setIsDraggingOver(true);
  };

  const onDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);
  };

  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);
    if (!disabled && !isUploading) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const onClick = () => {
    if (!disabled && !isUploading) {
      inputRef.current?.click();
    }
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if ((e.key === 'Enter' || e.key === ' ') && !disabled && !isUploading) {
      e.preventDefault();
      inputRef.current?.click();
    }
  };

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFiles(e.target.files);
    // Reset so same file can be re-selected
    e.target.value = '';
  };

  // CSS state
  const borderColor = isDraggingOver
    ? 'var(--color-brand-primary)'
    : isUploading
    ? 'var(--color-info)'
    : emphasize
    ? 'var(--color-brand-primary)'
    : 'var(--color-surface-border)';

  const borderWidth = emphasize && !isDraggingOver && !isUploading ? '3px' : '2px';
  const borderStyle = isDraggingOver ? 'solid' : 'dashed';

  const bgColor = isDraggingOver
    ? 'color-mix(in srgb, var(--color-brand-primary) 12%, var(--color-surface-muted))'
    : isUploading
    ? 'color-mix(in srgb, var(--color-info) 8%, var(--color-surface-muted))'
    : 'var(--color-surface-muted)';

  return (
    <>
      <style>{KEYFRAME_CSS}</style>
      <div
        role="button"
        tabIndex={disabled || isUploading ? -1 : 0}
        aria-label={t('attachmentDemo.dropzone.ariaLabel')}
        aria-describedby="dropzone-policy"
        aria-disabled={disabled || isUploading}
        onDragEnter={onDragEnter}
        onDragLeave={onDragLeave}
        onDragOver={onDragOver}
        onDrop={onDrop}
        onClick={onClick}
        onKeyDown={onKeyDown}
        data-testid="dropzone"
        style={{
          background: bgColor,
          border: `${borderWidth} ${borderStyle} ${borderColor}`,
          borderRadius: 'var(--radius-lg)',
          padding: 'var(--spacing-8)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 'var(--spacing-3)',
          textAlign: 'center',
          transition: 'border-color 0.15s, background 0.15s',
          cursor: disabled || isUploading ? (isUploading ? 'progress' : 'not-allowed') : 'pointer',
          minHeight: 160,
          marginBottom: 'var(--spacing-6)',
          position: 'relative',
          outline: 'none',
        }}
      >
        {/* Icon */}
        <span
          aria-hidden="true"
          style={{
            fontSize: 48,
            color: isUploading ? 'var(--color-info)' : 'var(--color-brand-primary)',
            opacity: 0.7,
            lineHeight: 1,
            display: 'block',
          }}
        >
          {isUploading ? '⏳' : '📂'}
        </span>

        {/* Title */}
        <span
          style={{
            fontSize: 'var(--font-size-lg)',
            fontWeight: 700,
            color: 'var(--color-text-primary)',
          }}
        >
          {isUploading
            ? t('attachmentDemo.dropzone.titleUploading')
            : isDraggingOver
            ? t('attachmentDemo.dropzone.titleActive')
            : t('attachmentDemo.dropzone.title')}
        </span>

        {/* Description */}
        {!isUploading && (
          <span
            style={{
              fontSize: 'var(--font-size-sm)',
              color: 'var(--color-text-secondary)',
            }}
          >
            {t('attachmentDemo.dropzone.desc')}
          </span>
        )}

        {/* Policy pill */}
        <span
          id="dropzone-policy"
          style={{
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-text-muted)',
            fontFamily: 'var(--font-family-mono)',
            background: 'var(--color-surface-elevated)',
            padding: 'var(--spacing-1) var(--spacing-3)',
            borderRadius: 'var(--radius-full)',
            border: '1px solid var(--color-surface-border)',
            marginTop: 'var(--spacing-2)',
          }}
        >
          {t('attachmentDemo.dropzone.policy').replace('5MB', formatBytes(MAX_FILE_SIZE_BYTES))}
        </span>

        {/* Progress bar during upload */}
        {isUploading && (
          <ProgressBar
            value={uploadProgress}
            fileName={uploadingFileName}
            ariaLabel={t('attachmentDemo.dropzone.progressLabel', { name: uploadingFileName ?? '' })}
          />
        )}

        {/* Hidden file input */}
        <input
          ref={inputRef}
          type="file"
          accept={ALLOWED_MIMES.join(',')}
          aria-hidden="true"
          tabIndex={-1}
          style={{ display: 'none' }}
          onChange={onInputChange}
          data-testid="dropzone-input"
        />
      </div>
    </>
  );
}
